import { isPlainObject } from 'remeda'
import { isElementDisabled } from '../helpers/components'
import { triggerForm } from '../helpers/utils'

// Private types ---------------------------------------------------------------

type CheckboxHook = {
  readonly el: HTMLElement
  readonly pushEvent: (event: string, payload: object) => void
  // Phoenix owns the hook instance and its cleanup resource.
  checkboxCleanup?: () => void
}

type DeferredToggle = Readonly<{
  event: string
  value: object
}>

type CheckboxElement = HTMLElement & {
  // Web Awesome exposes mutable state through its element API.
  checked: boolean
  indeterminate?: boolean
}

type UpdatableCheckboxElement = CheckboxElement & {
  readonly updateComplete: Promise<unknown>
}

// Private functions -----------------------------------------------------------

// Recognizes the shared checked-state API of Web Awesome checkboxes and switches.
const isCheckboxElement = (element: HTMLElement): element is CheckboxElement =>
  (element.tagName === 'WA-CHECKBOX' || element.tagName === 'WA-SWITCH') &&
  'checked' in element &&
  typeof element.checked === 'boolean'

// Detects controls that expose completion of their pending render.
const isUpdatableCheckboxElement = (
  element: CheckboxElement
): element is UpdatableCheckboxElement =>
  'updateComplete' in element && element.updateComplete instanceof Promise

// Applies server-owned checked and indeterminate attributes to the control.
const syncServerState = (element: HTMLElement): void => {
  if (!isCheckboxElement(element)) {
    return
  }

  element.checked = element.hasAttribute('checked')
  if ('indeterminate' in element) {
    element.indeterminate = element.hasAttribute('indeterminate')
  }
}

// Waits for the custom element upgrade and its initial render.
const waitUntilReady = async (element: HTMLElement): Promise<void> => {
  await window.customElements.whenDefined(element.localName)

  if (isCheckboxElement(element) && isUpdatableCheckboxElement(element)) {
    await element.updateComplete
  }
}

// Reapplies server state after upgrade while the hook remains mounted.
const setupServerStateSync = (element: HTMLElement): (() => void) => {
  let active = true

  syncServerState(element)
  waitUntilReady(element)
    .then(() => {
      if (active) {
        syncServerState(element)
      }
    })
    .catch(() => undefined)

  return () => {
    active = false
  }
}

// Recognizes the form notification already emitted by this hook.
const isRelayedChange = (event: Event): boolean =>
  event instanceof window.CustomEvent &&
  isPlainObject(event.detail) &&
  'facadeCheckbox' in event.detail &&
  event.detail.facadeCheckbox === true

// Relays each completed control change through the form's current value.
const setupFormChangeRelay = (element: HTMLElement): (() => void) => {
  let active = true
  let pending = false

  // aria-busy signals a server round-trip in flight; without a phx-change on
  // the carrier or its form no patch ever lands to clear it.
  const hasLiveChange =
    element
      .querySelector<HTMLInputElement>('[data-checkbox-value]')
      ?.hasAttribute('phx-change') ||
    element.closest('form[phx-change]') !== null

  // Claims native control events without intercepting our relayed change.
  const stopNativeEvent = (event: Event): boolean => {
    if (event.target !== element || isRelayedChange(event)) {
      return false
    }

    event.stopImmediatePropagation()

    if (hasLiveChange) {
      element.setAttribute('aria-busy', 'true')
    }

    return true
  }

  // Prevents the form from reading the control before its render finishes.
  const handleInput = (event: Event): void => {
    stopNativeEvent(event)
  }

  // Publishes the settled checked value once per pending render.
  const handleChange = (event: Event): void => {
    if (!stopNativeEvent(event)) {
      return
    }

    if (pending) {
      return
    }

    pending = true
    const updateComplete =
      isCheckboxElement(element) && isUpdatableCheckboxElement(element)
        ? element.updateComplete
        : Promise.resolve()

    updateComplete
      .then(() => {
        if (!active) {
          pending = false
          return
        }

        pending = false
        const carrier = element.querySelector('[data-checkbox-value]')

        if (carrier instanceof HTMLInputElement && isCheckboxElement(element)) {
          carrier.checked = element.checked
          triggerForm(carrier.id, 'change', undefined, element)
          return
        }

        element.dispatchEvent(
          new window.CustomEvent('change', {
            bubbles: true,
            composed: true,
            detail: { facadeCheckbox: true }
          })
        )
      })
      .catch(() => {
        pending = false
      })
  }

  element.addEventListener('input', handleInput, true)
  element.addEventListener('change', handleChange, true)

  return () => {
    active = false
    element.removeEventListener('input', handleInput, true)
    element.removeEventListener('change', handleChange, true)
  }
}

// Validates the server event and payload declared on a deferred control.
const readDeferredToggle = (
  element: HTMLElement
): DeferredToggle | undefined => {
  const event = element.dataset.deferEvent
  const encodedValue = element.dataset.deferValue ?? '{}'

  if (!event) {
    return undefined
  }

  try {
    const value: unknown = JSON.parse(encodedValue)

    return isPlainObject(value) ? { event, value } : undefined
  } catch {
    return undefined
  }
}

// Keeps a deferred checkbox unchanged until the server accepts the toggle.
const preventNativeToggle = (event: Event): void => {
  event.preventDefault()
  event.stopImmediatePropagation()
}

// Requests one server-owned toggle while guarding disabled and busy controls.
const requestDeferredToggle = (hook: CheckboxHook, event: Event): void => {
  preventNativeToggle(event)

  if (
    isElementDisabled(hook.el) ||
    hook.el.getAttribute('aria-busy') === 'true'
  ) {
    return
  }

  const toggle = readDeferredToggle(hook.el)

  if (!toggle) {
    return
  }

  hook.el.setAttribute('aria-busy', 'true')
  hook.pushEvent(toggle.event, toggle.value)
}

// Connects pointer and keyboard activation to the deferred toggle request.
const setupDeferredToggle = (hook: CheckboxHook): (() => void) => {
  // Requests a toggle from pointer activation.
  const handleClick = (event: MouseEvent): void =>
    requestDeferredToggle(hook, event)

  // Requests a toggle from the checkbox's Space key activation.
  const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === ' ') {
      requestDeferredToggle(hook, event)
    }
  }

  hook.el.addEventListener('click', handleClick, true)
  hook.el.addEventListener('keydown', handleKeydown, true)

  return () => {
    hook.el.removeEventListener('click', handleClick, true)
    hook.el.removeEventListener('keydown', handleKeydown, true)
  }
}

// Public hook -----------------------------------------------------------------

export const FacadeCheckbox = {
  // Connects server state and the appropriate form or deferred event flow.
  mounted(this: CheckboxHook): void {
    const cleanupServerState = setupServerStateSync(this.el)
    const cleanupDeferredToggle = this.el.hasAttribute('data-defer-toggle')
      ? setupDeferredToggle(this)
      : undefined
    const cleanupFormChangeRelay =
      !this.el.hasAttribute('data-defer-toggle') &&
      (this.el.querySelector('[data-checkbox-value]') ||
        this.el.closest('form[phx-change]'))
        ? setupFormChangeRelay(this.el)
        : undefined

    this.checkboxCleanup = () => {
      cleanupServerState()
      cleanupDeferredToggle?.()
      cleanupFormChangeRelay?.()
    }
  },

  // Applies the checked state delivered by the latest LiveView patch.
  updated(this: CheckboxHook): void {
    syncServerState(this.el)
  },

  // Releases listeners and prevents pending renders from relaying changes.
  destroyed(this: CheckboxHook): void {
    this.checkboxCleanup?.()
    this.checkboxCleanup = undefined
  }
}
